import { memo, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MembershipStatusCell from './MembershipStatusCell';
import type { Member, MembershipSummary } from '@/types';
import { MemberProfilePhotoData } from '@/types';
import type { MemberPhoto } from '@/types/photo';
import { useBulkMemberPhotos, useBulkMemberProfilePhotos } from '@/hooks';
import { useMembershipsToDisplay } from '@/utils';
import type { ChunkPhotoStatusReporter } from './memberChunkPhotoStatus';

interface MembersCardViewProps {
  /**
   * The revealed slice of the filtered roster, already split into fixed-size
   * chunks. Each chunk fetches its own photos — see MemberCardChunk.
   */
  chunks: Member[][];
  memberMemberships: Record<string, MembershipSummary[] | undefined>;
  areMembershipsLoading: boolean;
  /** Set when the bulk membership fetch failed — see MembershipStatusCell. */
  membershipsUnavailable?: boolean;
  /** Lets the page aggregate per-chunk uploaded-photo failures into one banner. */
  reportPhotoStatus: ChunkPhotoStatusReporter;
}

/**
 * Individual card component to handle membership logic per member
 */
const MemberCard = ({
  member,
  photo,
  uploadedPhoto,
  memberships,
  arePhotosLoading,
  areMembershipsLoading,
  membershipsUnavailable
}: {
  member: Member;
  photo: MemberPhoto | null | undefined;
  uploadedPhoto?: MemberProfilePhotoData | null;
  memberships: MembershipSummary[] | undefined;
  arePhotosLoading: boolean;
  areMembershipsLoading: boolean;
  membershipsUnavailable: boolean;
}) => {
  const navigate = useNavigate();

  const { memberships: displayMemberships } = useMembershipsToDisplay<MembershipSummary>(memberships);

  // Prefer the small variant, fall back to the large one — either can be null
  // on its own if only that path failed to sign.
  const webcamSrc = photo?.thumbnail_photo_url ?? photo?.large_photo_url ?? null;
  const webcamFallbackSrc = photo?.large_photo_url ?? null;
  const uploadedSrc = uploadedPhoto?.thumbnail_photo_url ?? uploadedPhoto?.large_photo_url ?? null;
  const uploadedFallbackSrc = uploadedPhoto?.large_photo_url ?? null;

  return (
    <div
      onClick={() => navigate(`/members/${member.id}`)}
      className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer p-6 h-full flex flex-col"
    >
      {/* Member Photo and Name */}
      <div className="flex flex-col items-center text-center mb-4 flex-grow">
        {/* Member Photo - Overlapping display: uploaded photo behind, webcam/placeholder in front */}
        {/* Both circles render the 150px `thumbnail` variant, not `large`: large
            is an 800px / ~200 KB JPEG and these are 128px circles, so the grid
            was pulling ~24x more image bytes than it could ever display. */}
        <div className="flex-shrink-0 mb-3 relative" style={{ width: uploadedSrc ? '148px' : '128px', height: '128px' }}>
          {arePhotosLoading ? (
            <div className="h-32 w-32 rounded-full bg-gray-200 animate-pulse" />
          ) : (
            <>
              {/* Uploaded photo (behind) - shown whenever it exists */}
              {uploadedSrc && (
                <img
                  src={uploadedSrc}
                  alt={`${member.first_name} ${member.last_name} uploaded`}
                  className="absolute right-0 top-0 h-32 w-32 rounded-full object-cover opacity-70"
                  style={{ zIndex: 0 }}
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (uploadedFallbackSrc && target.src !== uploadedFallbackSrc) {
                      target.src = uploadedFallbackSrc;
                    }
                  }}
                />
              )}
              {/* Front: Webcam photo if exists, otherwise placeholder icon */}
              {webcamSrc ? (
                <img
                  src={webcamSrc}
                  alt={`${member.first_name} ${member.last_name}`}
                  className="relative h-32 w-32 rounded-full object-cover"
                  style={{ zIndex: 10 }}
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (webcamFallbackSrc && target.src !== webcamFallbackSrc) {
                      target.src = webcamFallbackSrc;
                    }
                  }}
                />
              ) : (
                <div className="relative h-32 w-32 rounded-full bg-gray-100 flex items-center justify-center" style={{ zIndex: 10 }}>
                  <svg className="h-16 w-16 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
              )}
            </>
          )}
        </div>

        {/* Member Name */}
        <div>
          <div className="font-medium text-gray-900 text-lg">
            {member.first_name} {member.last_name}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {member.email}
          </div>
        </div>
      </div>

      {/* Membership Status */}
      <div className="mt-4 text-center space-y-2">
        {areMembershipsLoading ? (
          // A still-loading fetch must not render as "No Membership" — the empty
          // map is indistinguishable from genuinely uncovered (HOK-OS-50).
          <div className="mx-auto h-5 w-24 rounded-full bg-gray-200 animate-pulse" />
        ) : displayMemberships.length > 0 ? (
          displayMemberships.map((membership) => (
            <div key={membership.id}>
              <MembershipStatusCell membership={membership} />
            </div>
          ))
        ) : (
          <MembershipStatusCell membership={undefined} isUnavailable={membershipsUnavailable} />
        )}
      </div>
    </div>
  );
};

/**
 * One chunk of cards, owning the batched photo fetch for exactly the members it
 * renders.
 *
 * This is where the page stops paying roster-sized costs: previously the page
 * signed ~3,000 storage URLs (three size variants x ~1,000 members) so that the
 * dozen cards on screen could show a thumbnail. A chunk signs ~120.
 *
 * It renders a fragment, not a wrapper element, so the cards stay direct
 * children of the parent CSS grid.
 */
const MemberCardChunk = memo(function MemberCardChunk({
  chunkKey,
  members,
  memberMemberships,
  areMembershipsLoading,
  membershipsUnavailable,
  reportPhotoStatus,
}: {
  chunkKey: string;
  members: Member[];
  memberMemberships: Record<string, MembershipSummary[] | undefined>;
  areMembershipsLoading: boolean;
  membershipsUnavailable: boolean;
  reportPhotoStatus: ChunkPhotoStatusReporter;
}) {
  const memberIds = useMemo(() => members.map((member) => member.id), [members]);

  const { data: memberPhotos = {}, isLoading: arePhotosLoading } = useBulkMemberPhotos(memberIds);
  const {
    data: memberUploadedPhotos = {},
    isLoading: areUploadedPhotosLoading,
    error: uploadedPhotosError,
    refetch: refetchUploadedPhotos,
  } = useBulkMemberProfilePhotos(memberIds);

  // `refetch` travels by ref, not in the dependency array. React Query does not
  // guarantee its identity, and an unstable dep here would re-run the effect
  // every render: the cleanup clears this chunk's entry and the effect re-adds
  // it, producing a new failing-key array each pass and an endless render loop.
  // Assigned in a commit-phase effect, not during render: a render-phase write
  // is an impure render, which React is free to discard or replay.
  const refetchRef = useRef(refetchUploadedPhotos);
  const hasUploadedPhotosError = Boolean(uploadedPhotosError);

  useEffect(() => {
    refetchRef.current = refetchUploadedPhotos;
  });

  useEffect(() => {
    reportPhotoStatus(chunkKey, {
      hasError: hasUploadedPhotosError,
      retry: () => refetchRef.current(),
    });
    return () => reportPhotoStatus(chunkKey, null);
  }, [chunkKey, hasUploadedPhotosError, reportPhotoStatus]);

  return (
    <>
      {members.map((member) => (
        <MemberCard
          key={member.id}
          member={member}
          photo={memberPhotos[member.id]}
          uploadedPhoto={memberUploadedPhotos[member.id]}
          memberships={memberMemberships[member.id]}
          arePhotosLoading={arePhotosLoading || areUploadedPhotosLoading}
          areMembershipsLoading={areMembershipsLoading}
          membershipsUnavailable={membershipsUnavailable}
        />
      ))}
    </>
  );
});

export default function MembersCardView({
  chunks,
  memberMemberships,
  areMembershipsLoading,
  membershipsUnavailable = false,
  reportPhotoStatus,
}: MembersCardViewProps) {
  if (!chunks.length) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg">No members found</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {chunks.map((chunk, index) => (
        <MemberCardChunk
          // Index-keyed on purpose: chunk N always holds the same slice of the
          // current filtered list, so reusing the component across a reveal is
          // what keeps already-mounted chunks from re-fetching.
          key={`chunk-${index}`}
          chunkKey={`chunk-${index}`}
          members={chunk}
          memberMemberships={memberMemberships}
          areMembershipsLoading={areMembershipsLoading}
          membershipsUnavailable={membershipsUnavailable}
          reportPhotoStatus={reportPhotoStatus}
        />
      ))}
    </div>
  );
}
